import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  Users, Building2, Kanban, Home, ChevronDown, ChevronRight as ChevronRightIcon, ChevronLeft,
  UserCheck, Building, Flame, Ban, CalendarCheck, HeartHandshake, Settings, Smartphone, Globe, ClipboardList, Send,
  ShoppingBag, Bookmark, FileText, ScrollText, Receipt, Bot, Link2, LayoutDashboard as IconeDashboard,
  Wallet, BellRing, Barcode, TrendingUp, CalendarDays, FileSpreadsheet, FileCode, PieChart, ArrowLeftRight, Landmark, LayoutDashboard, Repeat, GitCompare, History,
  RefreshCw,
} from 'lucide-react';

const FINANCEIRO_NAV = [
  { to: '/financeiro/painel', icon: LayoutDashboard, label: 'Painel' },
  { to: '/financeiro/cobrancas', icon: BellRing, label: 'Cobranças' },
  { to: '/financeiro/boletos', icon: Barcode, label: 'Boletos' },
  { to: '/financeiro/reajustes', icon: TrendingUp, label: 'Reajustes' },
  { to: '/financeiro/contas-a-pagar', icon: CalendarDays, label: 'Contas a Pagar' },
  { to: '/financeiro/documentos', icon: FileSpreadsheet, label: 'Documentos' },
  { to: '/financeiro/nfe', icon: FileCode, label: 'Notas Fiscais' },
  { to: '/financeiro/faturamento', icon: Receipt, label: 'Faturamento' },
  { to: '/financeiro/fluxo-de-caixa', icon: PieChart, label: 'Fluxo de Caixa' },
  { to: '/financeiro/movimentos', icon: ArrowLeftRight, label: 'Movimentos' },
  { to: '/financeiro/conciliacao', icon: GitCompare, label: 'Conciliação' },
  { to: '/financeiro/recorrencias', icon: Repeat, label: 'Recorrências' },
  { to: '/financeiro/contas', icon: Landmark, label: 'Contas e cadastros' },
  { to: '/financeiro/auditoria', icon: History, label: 'Auditoria', gestao: true },
];

// Admin, gerente e financeiro; com módulos restritos, só quem recebeu o módulo 'financeiro'
function podeVerFinanceiro(user) {
  if (!user || !['admin', 'gerente', 'financeiro'].includes(user.perfil)) return false;
  if (user.perfil === 'admin') return true;
  const mods = user.modulos_permitidos;
  return !mods || !mods.length || mods.includes('financeiro');
}

const VENDAS_NAV = [
  { to: '/vendas/clientes', icon: Users, label: 'Clientes' },
  { to: '/vendas/pre-reservas', icon: Bookmark, label: 'Pré-Reservas' },
  { to: '/vendas/propostas', icon: FileText, label: 'Propostas' },
  { to: '/vendas/contratos', icon: ScrollText, label: 'Contratos' },
  { to: '/vendas/boletos', icon: Receipt, label: 'Boletos' },
];

const PARCERIAS_NAV = [
  { to: '/vendas/corretores', icon: UserCheck, label: 'Corretores' },
  { to: '/vendas/parceiros', icon: HeartHandshake, label: 'Parceiros' },
  { to: '/vendas/funil-corretores', icon: Kanban, label: 'Funil de Leads Corretores', end: true },
  { to: '/vendas/funil-corretores?mensagens=1', icon: Send, label: 'Mensagens Automáticas', semDestaque: true },
  { to: '/vendas/corretores-reaquecidos', icon: Flame, label: 'Corretores Reaquecidos' },
  { to: '/vendas/imobiliarias', icon: Building, label: 'Imobiliárias' },
  { to: '/vendas/eventos', icon: CalendarCheck, label: 'Eventos' },
  { to: '/vendas/elo', icon: Link2, label: 'Elo' },
];

// Corretor interno: menu enxuto, só com os módulos liberados para ele
const MENU_INTERNO = [
  { to: '/dashboard', icon: IconeDashboard, label: 'Dashboard' },   // sem módulo: todo corretor interno vê (dados filtrados no servidor)
  { modulo: 'leads', to: '/leads', icon: Users, label: 'Meus Leads', end: true },
  { modulo: 'leads', to: '/leads/kanban', icon: Kanban, label: 'Funil de Leads' },
  { modulo: 'leads', to: '/leads/visitas', icon: ClipboardList, label: 'Registro de Visitas' },
  { modulo: 'pipeline', to: '/pipeline', icon: Kanban, label: 'Pipeline' },
  { modulo: 'empreendimentos', to: '/empreendimentos', icon: Building2, label: 'Empreendimentos' },
  { modulo: 'imoveis', to: '/imoveis', icon: Home, label: 'Imóveis' },
];
const itensInterno = (user) => MENU_INTERNO.filter(i => !i.modulo || (user?.modulos_permitidos || []).some(m => m === i.modulo || m.startsWith(i.modulo + '.')));

// admin sempre; gerente com módulos restritos só se tiver 'roleta'
function podeVerRoleta(user) {
  if (!user || !['admin', 'gerente'].includes(user.perfil)) return false;
  const mods = user.modulos_permitidos;
  return user.perfil === 'admin' || !mods || !mods.length || mods.includes('roleta');
}

function getUser() {
  try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; }
}

function CollapsedNavItem({ to, icon: Icon, label }) {
  return (
    <NavLink
      to={to}
      title={label}
      className={({ isActive }) =>
        `flex items-center justify-center w-10 h-10 mx-auto rounded-lg transition-colors ${
          isActive ? 'bg-secondary text-white' : 'text-blue-200 hover:bg-blue-800'
        }`
      }
    >
      <Icon size={18} />
    </NavLink>
  );
}

function NavGroup({ icon: Icon, label, open, onToggle, children }) {
  return (
    <div className="pt-1">
      <button
        onClick={onToggle}
        className="flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors text-sm font-medium text-blue-200 hover:bg-blue-800 w-full"
      >
        <Icon size={18} />
        <span className="flex-1 text-left">{label}</span>
        {open ? <ChevronDown size={14} /> : <ChevronRightIcon size={14} />}
      </button>
      {open && (
        <div className="ml-4 space-y-0.5 mt-0.5">
          {children}
        </div>
      )}
    </div>
  );
}

// Subgrupo aninhado dentro de um NavGroup (estilo menor, igual ao LeadsGroup)
function SubNavGroup({ icon: Icon, label, open, onToggle, children }) {
  return (
    <div className="pt-0.5">
      <button
        onClick={onToggle}
        className="flex items-center gap-3 px-4 py-2 rounded-lg transition-colors text-xs font-medium text-blue-300 hover:bg-blue-800 w-full"
      >
        <Icon size={15} />
        <span className="flex-1 text-left">{label}</span>
        {open ? <ChevronDown size={12} /> : <ChevronRightIcon size={12} />}
      </button>
      {open && (
        <div className="ml-4 space-y-0.5 mt-0.5">
          {children}
        </div>
      )}
    </div>
  );
}

function SubNavLink({ to, icon: Icon, label, end, semDestaque }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-2 rounded-lg transition-colors text-xs font-medium ${
          isActive && !semDestaque ? 'bg-secondary text-white' : 'text-blue-300 hover:bg-blue-800'
        }`
      }
    >
      <Icon size={15} />
      {label}
    </NavLink>
  );
}

// Submenu CRM > Leads (com botões de atalho no topo)
function LeadsGroup({ open, onToggle }) {
  return (
    <div className="pt-0.5">
      <button
        onClick={onToggle}
        className="flex items-center gap-3 px-4 py-2 rounded-lg transition-colors text-xs font-medium text-blue-300 hover:bg-blue-800 w-full"
      >
        <Users size={15} />
        <span className="flex-1 text-left">Leads</span>
        {open ? <ChevronDown size={12} /> : <ChevronRightIcon size={12} />}
      </button>
      {open && (
        <div className="ml-4 space-y-0.5 mt-0.5">
          <NavLink
            to="/leads"
            end
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-2 rounded-lg transition-colors text-xs font-medium ${
                isActive ? 'bg-secondary text-white' : 'text-blue-300 hover:bg-blue-800'
              }`
            }
          >
            <Users size={14} /> Todos os Leads
          </NavLink>
          <NavLink
            to="/leads/kanban"
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-2 rounded-lg transition-colors text-xs font-medium ${
                isActive ? 'bg-secondary text-white' : 'text-blue-300 hover:bg-blue-800'
              }`
            }
          >
            <Kanban size={14} /> Funil de Leads
          </NavLink>
          <NavLink
            to="/leads/visitas"
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-2 rounded-lg transition-colors text-xs font-medium ${
                isActive ? 'bg-secondary text-white' : 'text-blue-300 hover:bg-blue-800'
              }`
            }
          >
            <ClipboardList size={14} /> Registro de Visitas
          </NavLink>
          <NavLink
            to="/leads-reaquecidos"
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-2 rounded-lg transition-colors text-xs font-medium ${
                isActive ? 'bg-secondary text-white' : 'text-blue-300 hover:bg-blue-800'
              }`
            }
          >
            <Flame size={14} /> Leads Reaquecidos
          </NavLink>
          <NavLink
            to="/blacklist"
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-2 rounded-lg transition-colors text-xs font-medium ${
                isActive ? 'bg-secondary text-white' : 'text-blue-300 hover:bg-blue-800'
              }`
            }
          >
            <Ban size={14} /> Blacklist
          </NavLink>
        </div>
      )}
    </div>
  );
}

export default function Sidebar({ open, onToggle }) {
  const [crmOpen, setCrmOpen] = useState(true);
  const [leadsOpen, setLeadsOpen] = useState(false);
  const [vendasOpen, setVendasOpen] = useState(true);
  const [financeiroOpen, setFinanceiroOpen] = useState(false);
  const [parceriasOpen, setParceriasOpen] = useState(true);
  const [configOpen, setConfigOpen] = useState(false);

  const interno = getUser()?.perfil === 'corretor_interno';
  const flatItems = interno ? itensInterno(getUser()) : [
    { to: '/dashboard', icon: IconeDashboard, label: 'Dashboard' },
    { to: '/leads', icon: Building2, label: 'CRM' },
    { to: '/vendas/clientes', icon: ShoppingBag, label: 'Vendas' },
    { to: '/vendas/corretores', icon: HeartHandshake, label: 'Parcerias' },
    ...(podeVerFinanceiro(getUser()) ? [{ to: '/financeiro/painel', icon: Wallet, label: 'Financeiro' }] : []),
    { to: '/configuracoes/whatsapp', icon: Settings, label: 'Configurações' },
  ];

  return (
    <div
      className={`bg-primary text-white flex flex-col shadow-lg transition-all duration-300 ease-in-out flex-shrink-0 relative z-30 ${
        open ? 'w-64' : 'w-14'
      }`}
    >
      <div
        className={`border-b border-blue-800 flex items-center flex-shrink-0 ${
          open ? 'p-6 justify-between' : 'p-2 justify-center'
        }`}
      >
        {open ? (
          <>
            <img src="/logo-ra-branca.png" alt="RA Empreendimentos" className="h-20 w-auto" />
            <button
              onClick={onToggle}
              title="Recolher menu"
              className="text-blue-300 hover:text-white transition-colors p-1 rounded"
            >
              <ChevronLeft size={18} />
            </button>
          </>
        ) : (
          <button
            onClick={onToggle}
            title="Expandir menu"
            className="text-blue-300 hover:text-white transition-colors p-1 rounded"
          >
            <ChevronRightIcon size={18} />
          </button>
        )}
      </div>

      <nav className={`flex-1 overflow-y-auto overflow-x-hidden ${open ? 'p-3 space-y-0.5' : 'py-3 space-y-1'}`}>
        {open ? (
          <>
            {interno && itensInterno(getUser()).map(({ modulo, ...item }) => <SubNavLink key={item.to} {...item} />)}
            {!interno && <SubNavLink to="/dashboard" icon={IconeDashboard} label="Dashboard" />}
            {!interno && <NavGroup icon={Building2} label="CRM" open={crmOpen} onToggle={() => setCrmOpen(v => !v)}>
              <LeadsGroup open={leadsOpen} onToggle={() => setLeadsOpen(v => !v)} />
              <SubNavLink to="/empreendimentos" icon={Building2} label="Empreendimentos" />
              <SubNavLink to="/imoveis" icon={Home} label="Imóveis" />
              <SubNavLink to="/pipeline" icon={Kanban} label="Pipeline" />
            </NavGroup>}

            {!interno && <NavGroup icon={ShoppingBag} label="Vendas" open={vendasOpen} onToggle={() => setVendasOpen(v => !v)}>
              {VENDAS_NAV.map(item => <SubNavLink key={item.to} {...item} />)}
            </NavGroup>}

            {!interno && <NavGroup icon={HeartHandshake} label="Parcerias" open={parceriasOpen} onToggle={() => setParceriasOpen(v => !v)}>
              {PARCERIAS_NAV.map(item => <SubNavLink key={item.to} {...item} />)}
            </NavGroup>}

            {!interno && podeVerFinanceiro(getUser()) && (
              <NavGroup icon={Wallet} label="Financeiro" open={financeiroOpen} onToggle={() => setFinanceiroOpen(v => !v)}>
                {FINANCEIRO_NAV.filter(item => !item.gestao || ['admin', 'gerente'].includes(getUser()?.perfil)).map(({ gestao, ...item }) => <SubNavLink key={item.to} {...item} />)}
              </NavGroup>
            )}

            {!interno && <NavGroup icon={Settings} label="Configurações" open={configOpen} onToggle={() => setConfigOpen(v => !v)}>
              <SubNavLink to="/configuracoes/whatsapp" icon={Smartphone} label="WhatsApp" />
              {['admin', 'gerente'].includes(getUser()?.perfil) && <SubNavLink to="/configuracoes/atendente-ia" icon={Bot} label="Atendente IA" />}
              {['admin', 'gerente'].includes(getUser()?.perfil) && <SubNavLink to="/configuracoes/site" icon={Globe} label="Site" />}
              {podeVerRoleta(getUser()) && <SubNavLink to="/configuracoes/roleta" icon={RefreshCw} label="Roleta de leads" />}
              {['admin', 'gerente'].includes(getUser()?.perfil) && <SubNavLink to="/configuracoes/usuarios" icon={Users} label="Usuários" />}
              {getUser()?.perfil === 'admin' && <SubNavLink to="/configuracoes/integracoes" icon={Settings} label="Integrações" />}
            </NavGroup>}
            {interno && (
              <a href="https://vendas.raempreendimentosmga.com.br" target="_blank" rel="noreferrer"
                className="flex items-center gap-3 px-4 py-2 rounded-lg transition-colors text-xs font-medium text-blue-300 hover:bg-blue-800">
                <ShoppingBag size={15} /> Portal de vendas
              </a>
            )}
          </>
        ) : (
          <>
            {flatItems.map(({ to, icon: Icon, label }) => (
              <CollapsedNavItem key={to} to={to} icon={Icon} label={label} />
            ))}
          </>
        )}
      </nav>

      {open && (
        <div className="p-4 border-t border-blue-800 flex-shrink-0">
          <p className="text-xs text-blue-400">RACRM — Maringá/PR</p>
        </div>
      )}
    </div>
  );
}
